import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { LoggerService } from "@teammood/shared/util/logger";
import { IUser } from '@teammood/shared/util/user';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { firstValueFrom } from "rxjs";
import { RedirectPathService } from '../shared/util/redirect/redirect-path.service';
import { ActivatedRoute, ActivatedRouteSnapshot } from "@angular/router";
import { UrlService } from "@teammood/shared/util/url";

const AUTH_TOKEN_NAME = 'token';
const AUTH_TOKEN_PREFIX = 'teammood';

export const AUTH_TOKEN_KEY = `${AUTH_TOKEN_PREFIX}_${AUTH_TOKEN_NAME}`;

export interface IAuthService {
  getAuthProvider(email: string): Promise<AuthProviderData>;
  
  login: (credentials: { email: string, password: string }) => Promise<AuthenticatedUser>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;

  isAuthenticated(): boolean;
  getToken(): string | null;
  setToken(auth_token: string): void;
  removeToken(): void;
  
  getPayload(): TeamMoodJWTPayload;

  doAutoAuth(): Promise<string>
}

@Injectable({
  providedIn: 'root'
})
export class AuthService implements IAuthService {

  constructor(
    private http: HttpClient,
    private logger: LoggerService,
    private redirectPathService: RedirectPathService,
    private urlService: UrlService,
    private route: ActivatedRoute,
  ) { }

  async getAuthProvider(email: string): Promise<AuthProviderData> {
    return await firstValueFrom(this.http.post<AuthProviderData>('/api/auth/get_provider', { email }))
  }

  async login(credentials: { email: string, password: string }): Promise<AuthenticatedUser> {
    const user = await firstValueFrom(this.http.post<AuthenticatedUser>('/authenticate', credentials));
    this.setToken(user.token);
    return user;
  }

  async logout(): Promise<void> {
    await firstValueFrom(this.http.post('/logout', {},{responseType: 'text'}));
    this.logger.info('Successfully logged out on server side.');
    this.removeToken();
    this.redirectPathService.resetCookie();
  }
  
  async resetPassword(email: string): Promise<void> {
    await firstValueFrom(this.http.post(`/reset/email/${email}`, {}, {responseType: 'text'}))

    return;
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getToken() {
    return window.localStorage.getItem(AUTH_TOKEN_KEY);
  }

  setToken(auth_token: string): void {
    window.localStorage.setItem(AUTH_TOKEN_KEY, auth_token);
  }

  removeToken(): void {
    window.localStorage.removeItem(AUTH_TOKEN_KEY);
  }

  getPayload(): TeamMoodJWTPayload {
    return jwtDecode<TeamMoodJWTPayload>(this.getToken());
  }

  urlContainsAuthToken(activatedRoute: ActivatedRouteSnapshot): boolean {
    return !!activatedRoute.queryParams['auth_token'];
  }

  async doAutoAuth(): Promise<string> {

    const auth_token: string = this.route.snapshot.queryParams['auth_token'];

    if (auth_token) {
      this.logger.info('Authenticating user...');

      const cleanup_token: (token: string) => string = (token: string) =>{
        if (auth_token.indexOf('#') !== -1) { // may contains an anchor
          return auth_token.substring(0, auth_token.indexOf('#'));
        } else {
          return token;
        }
      }

      const cleaned_auth_token = cleanup_token(auth_token);
      this.setToken(cleaned_auth_token);

      const jwt_payload = this.getPayload();

      if (jwt_payload && jwt_payload.user_id && window['trackJs']) {
        window['trackJs'].configure({userId: jwt_payload.user_id});
      }

      // Hide the token from the url
      await this.urlService.removeAuthTokenQueryParam();

      return cleaned_auth_token;
  } else {
    return null;}
  }
}

interface AuthProviderData {
  auth_type: string,
  password_set: boolean
  redirect: string
}

type TeamMoodJWTPayload = JwtPayload & {
  user_id: string
}

export type AuthenticatedUser = IUser & {
  token: string;
}

export function isAuthenticatedUser(user: IUser): user is AuthenticatedUser {
  return typeof user === 'object' &&
      user !== null &&
      typeof (user as Record<string, any>).token === 'string' &&
      typeof (user as Record<string, any>).redirect === 'string';
}
